import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { theme, toggleTheme } = useTheme();
    const { t } = useLanguage();
    const location = useLocation();

    const navigation = [
        { name: t('nav.home'), href: '/' },
        { name: t('nav.menu'), href: '/menu' },
        { name: t('nav.about'), href: '/about' },
        { name: t('nav.gallery'), href: '/gallery' },
        { name: t('nav.contact'), href: '/contact' },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
                ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg'
                : 'bg-transparent'
                }`}
        >
            <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20 w-full">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-2">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="text-2xl lg:text-3xl font-serif font-bold text-gradient"
                        >
                            <img
                                src="images/logo.png"
                                alt={t('alt.logo')}
                                className="h-12 lg:h-12 rounded-full object-cover"
                            />
                        </motion.div>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center space-x-8">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`relative font-medium transition-colors duration-200 hover:text-primary-600 ${location.pathname === item.href
                                    ? 'text-primary-600'
                                    : 'text-gray-700 dark:text-gray-300'
                                    }`}
                            >
                                {item.name}
                                {location.pathname === item.href && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600"
                                    />
                                )}
                            </Link>
                        ))}
                    </nav>

                    {/* Right side buttons */}
                    <div className="flex items-center space-x-2 lg:space-x-4">
                        {/* Language selector */}
                        <div className="hidden sm:block">
                            <LanguageSelector />
                        </div>


                        {/* Theme toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-secondary-800 hover:bg-gray-200 dark:hover:bg-secondary-700 transition-colors"
                            aria-label={t('aria.toggleTheme')}
                        >
                            {theme === 'light' ? (
                                <Moon className="w-4 h-4 lg:w-5 lg:h-5" />
                            ) : (
                                <Sun className="w-4 h-4 lg:w-5 lg:h-5" />
                            )}
                        </button>

                        {/* Reservation button */}
                        <Link
                            to="/reservation"
                            className="hidden lg:block btn-primary text-sm px-4 py-2"
                        >
                            {t('hero.bookNow')}
                        </Link>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="lg:hidden p-2 rounded-lg bg-gray-100 dark:bg-secondary-800 hover:bg-gray-200 dark:hover:bg-secondary-700 transition-colors"
                            aria-label={t('aria.toggleMenu')}
                        >
                            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
                    >
                        <div className="container mx-auto max-w-7xl px-4 py-4">
                            <nav className="flex flex-col space-y-4">
                                {navigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`font-medium transition-colors duration-200 hover:text-primary-600 ${location.pathname === item.href
                                            ? 'text-primary-600'
                                            : 'text-gray-700 dark:text-gray-300'
                                            }`}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                                <div className="pt-4 border-t border-gray-200 dark:border-secondary-700 space-y-4">
                                    {/* Language Selector for Mobile */}
                                    <div className="sm:hidden">
                                        <LanguageSelector />
                                    </div>

                                    <Link
                                        to="/reservation"
                                        className="block w-full text-center btn-primary"
                                    >
                                        {t('hero.bookNow')}
                                    </Link>
                                </div>
                            </nav>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
